import React from 'react'
import {appApi, toast, useDiagnosticReport} from '../lib'
import {IconBug, IconHome, IconReset, IconSignOut} from '../Icons'
import {Box, Button, Container, Fab} from '@mui/material'
import {ActionHeader, AppMsg} from '.'
import {NetworkError} from '@data-client/react'
import {useAsync} from 'react-use'

export function NotFoundError() {
  return (
    <Container maxWidth="sm">
      <ActionHeader title="Unknown Request" />
      <AppMsg severity="error">
        The requested information is
        <br />
        unknown or unavailable
      </AppMsg>
      <Box textAlign="center">
        <Fab href="/" variant="extended" sx={{minWidth: 300}} color="primary">
          <IconHome /> &nbsp; Return to Account
        </Fab>
      </Box>
    </Container>
  )
}

function AppError({status}: {status?: number}) {
  const diag = useDiagnosticReport()
  return (
    <Container maxWidth="md">
      <ActionHeader title="Error Report" />
      <Box my={3}>
        Apologies for the inconvenience. This exception has been reported to the technical team. Some options to
        consider:
      </Box>
      <Box my={1} mx={2}>
        <Button onClick={() => window.location.reload()} color="primary">
          <IconReset /> &nbsp; Retry Request
        </Button>
      </Box>
      <Box my={1} mx={2}>
        <Button onClick={diag.resetSession} color="primary">
          <IconSignOut /> &nbsp; Reset Session
        </Button>
      </Box>
      <Box my={1} mx={2}>
        <Button onClick={diag.toggle} color="primary">
          <IconBug /> &nbsp; Diagnostic Report
        </Button>
      </Box>
      <diag.Report />
    </Container>
  )
}

type ErrorReportProps = {
  location: string
  children: React.ReactElement | React.ReactElement[]
}
type ErrorReportState = {
  error?: Error & {status: number}
  errorInfo?: React.ErrorInfo | null
  msg?: string
}
export class ErrorReport extends React.PureComponent<ErrorReportProps, ErrorReportState> {
  constructor(props: ErrorReportProps) {
    super(props)
    this.state = {}
  }

  override async componentDidCatch(error: Error & {status: number}, errorInfo: React.ErrorInfo) {
    this.setState({error, errorInfo})

    if (error.status === 404) {
      return
    }
    try {
      const data = {
        scope: localStorage.getItem('session.scope'),
        error: {
          path: window.location.pathname,
          name: error.name,
          message: error.message,
        },
        component: (errorInfo.componentStack || '')
          .split('\n')
          .slice(0, 9)
          .map(i => i.replace(/^\s+at\s+/u, ''))
          .filter(i => i.indexOf(' ') !== -1)
          .map(i => i.split(' ').shift())
          .join(' : '),
      }
      const msg = toast('loading', 'Sending Error Report')
      const res = await appApi('POST', '/error', data) // TODO: throttle
      msg.succeed()
      const reply = await res.json()
      this.setState({msg: reply.error || reply.msg})
    } catch (err) {
      console.error('failed to send error report:', err)
      this.setState({msg: String(err)})
    }
  }

  override componentDidUpdate(prevProps: ErrorReportProps) {
    if (prevProps.location !== this.props.location) {
      // stop showing error report on route change
      this.setState({errorInfo: null})
    }
  }

  override render() {
    if (this.state.errorInfo) {
      if (this.state.error?.status === 404) {
        return <NotFoundError />
      }
      return <AppError status={this.state.error?.status} />
    }
    return this.props.children
  }
}

export function ApiError({error}: {error: NetworkError}) {
  useAsync(async () => {
    if (error.status !== 404) {
      try {
        const data = {
          session: localStorage.getItem('session'),
          error: {
            path: window.location.pathname,
            name: error.name,
            message: error.message,
            // stack: error.stack?.split('\n').slice(1, 10).map(i => i.replace(/^\s+at\s+/u, '')),
          },
        }
        const msg = toast('loading', 'Sending Error Report')
        await appApi('POST', '/error', data) // TODO: throttle
        msg.succeed()
      } catch (err) {
        console.error('failed to send error report:', err)
      }
    }
  }, [])
  return <Container maxWidth="md">{error.status === 404 ? <NotFoundError /> : <AppError />}</Container>
}
